// GEAH — Global Entity Action Handler
// Single source of truth for entity-level DELETE / ARCHIVE / RESTORE.
//
// Any page that needs to delete a record MUST call:
//
//     await entityClient.delete('customer', id, { displayName })
//
// instead of `axios.delete('/customers/' + id)`. The wrapper:
//
//   1. Calls the canonical DELETE endpoint.
//   2. Catches HTTP 409 + standardized BLOCKED_DEPENDENCIES payload.
//   3. Triggers the global Delete-Guard modal with dependency counts.
//   4. If the user clicks "Archive Instead", hits POST /api/archive/{entity}/{id}.
//   5. Returns one of: { ok: true, mode: 'deleted' | 'archived' } or
//      { ok: false, reason: 'cancelled' | 'error', error }.
//
// The page only cares about the outcome — no per-page modal duplication.

import { axiosInstance } from '../App';

// Canonical entity → DELETE endpoint mapping. Add new entities here as
// they get archive support.
const DELETE_ENDPOINTS = {
  customer:       (id) => `/customers/${id}`,
  supplier:       (id) => `/suppliers/${id}`,
  project:        (id) => `/projects/${id}`,
  invoice:        (id) => `/invoices/${id}`,
  payment:        (id) => `/payments/${id}`,
  inventory:      (id) => `/inventory/${id}`,
  employee:       (id) => `/employees/${id}`,
  cost_center:    (id) => `/cost-centers/${id}`,
  subcontractor:  (id) => `/subcontractors/${id}`,
  subcontract:    (id) => `/subcontracts/${id}`,
  quotation:      (id) => `/quotations/${id}`,
  credit_note:    (id) => `/credit-notes/${id}`,
};

// Module-level reference to the open() function of the global modal.
// The DeleteGuardProvider registers itself here at mount time.
let _openModal = null;
export function _registerDeleteGuardModal(fn) { _openModal = fn; }

function unwrap409(err) {
  // Pull the standardized payload from any of these shapes:
  //   { detail: { action, recommended_action, ... } }
  //   { action, recommended_action, ... }
  const data = err?.response?.data;
  if (!data) return null;
  if (data.action === 'BLOCKED_DEPENDENCIES') return data;
  if (data.detail?.action === 'BLOCKED_DEPENDENCIES') return data.detail;
  return null;
}

async function _doDelete(entity, id) {
  const builder = DELETE_ENDPOINTS[entity];
  if (!builder) throw new Error(`entityClient: unknown entity '${entity}'`);
  return axiosInstance.delete(builder(id));
}

async function _doArchive(entity, id, reason) {
  return axiosInstance.post(`/archive/${entity}/${id}`, { reason: reason || null });
}

async function _doRestore(entity, id) {
  return axiosInstance.post(`/archive/${entity}/${id}/restore`);
}

export const entityClient = {
  /**
   * Try to DELETE an entity. On 409 BLOCKED_DEPENDENCIES, opens the
   * global modal and lets the user archive instead.
   *
   * @returns {Promise<{ok: boolean, mode: 'deleted'|'archived'|'cancelled', error?: any}>}
   */
  async delete(entity, id, { displayName, askConfirmFirst = true } = {}) {
    if (askConfirmFirst) {
      const ok = window.confirm(
        displayName
          ? `هل تريد حذف "${displayName}"؟ سيتم منع الحذف تلقائياً إذا كان مرتبطاً بسجلات أخرى.`
          : 'هل أنت متأكد من الحذف؟'
      );
      if (!ok) return { ok: false, mode: 'cancelled' };
    }
    try {
      await _doDelete(entity, id);
      return { ok: true, mode: 'deleted' };
    } catch (err) {
      const payload = unwrap409(err);
      if (!payload) {
        return { ok: false, mode: 'error', error: err };
      }
      // Trigger the global modal — returns 'archive' | 'cancel'.
      if (!_openModal) {
        // Fallback if provider missing — alert + return.
        const summary = Object.entries(payload.dependencies)
          .map(([k, v]) => `${k}: ${v}`).join(', ');
        window.alert(`لا يمكن حذف هذا السجل. مرتبط بـ: ${summary}\nاستخدم الأرشفة من /archive-center.`);
        return { ok: false, mode: 'blocked', payload };
      }
      const decision = await _openModal({
        entity, id, displayName, dependencies: payload.dependencies,
        details: payload.details, reason: payload.reason,
      });
      if (decision.action === 'archive') {
        await _doArchive(entity, id, decision.reason);
        return { ok: true, mode: 'archived' };
      }
      return { ok: false, mode: 'cancelled' };
    }
  },

  /** Direct archive — bypasses the DELETE attempt. */
  async archive(entity, id, reason = null) {
    await _doArchive(entity, id, reason);
    return { ok: true, mode: 'archived' };
  },

  /** Restore an archived record. */
  async restore(entity, id) {
    await _doRestore(entity, id);
    return { ok: true, mode: 'restored' };
  },

  /** Inspect dependents without taking any destructive action. */
  async dependents(entity, id) {
    const { data } = await axiosInstance.get(`/archive/${entity}/${id}/dependents`);
    return data;
  },
};

export default entityClient;
